/** @format */
import { Injectable } from '@angular/core';
import { dateUtils } from 'common-typescript/constants';
import { StudyPeriod, StudyPeriodLocator, StudyTerm, StudyYear } from 'common-typescript/types';
import moment from 'moment';

/**
 * Number of such study terms to be shown that end after the study right.
 */
export const STUDY_TERMS_VISIBLE_AFTER_STUDY_RIGHT: number = 4;

@Injectable()
export class TimelineTrimService {
    /**
     * Returns a subarray of {@link studyYears}, where:
     * a) none of the study years end before {@link timelineStartDate}
     * b) study years start on year {@link inclusiveTimelineEndYearBasedOnStudyRight} or earlier
     * OR contain a period having {@link lastPlannedPeriodLocator} as {@link StudyPeriod.locator}.
     *
     * {@link studyRightEndDate} is an exclusive end date of the study right. If it is provided:
     * * It is expected to be later than {@link timelineStartDate}.
     * * Only a limited number of non-planned years (and terms) following it are returned.
     *
     * If {@link lastPlannedPeriodLocator} is provided:
     * * It is expected to be no earlier than {@link timelineStartDate}.
     * * Its containing study term and the preceding terms are at least returned.
     *
     * {@link studyYears} is expected to contain the study year containing {@link timelineStartDate},
     * and also, if {@link lastPlannedPeriodLocator} is provided, a study year containing a respective period too.
     *
     * The last returned year may have been rebuilt to contain only some of the original study terms.
     */
    trimTimeline(
        studyYears: readonly StudyYear[],
        timelineStartDate: moment.Moment,
        studyRightEndDate: moment.Moment | null,
        inclusiveTimelineEndYearBasedOnStudyRight: number,
        lastPlannedPeriodLocator: StudyPeriodLocator | null,
    ): StudyYear[] {
        // remove the possible extra study year from the beginning, that was fetched just in case
        const allPossibleStudyYears: StudyYear[] = studyYears
            .filter(studyYear => timelineStartDate.isBefore(dateUtils.createMoment(studyYear.valid.endDate)))
            .toSorted((a, b) => dateUtils.createMoment(a.valid.startDate).diff(dateUtils.createMoment(b.valid.startDate)));
        if (!allPossibleStudyYears.length) {
            return allPossibleStudyYears;
        }

        // 1. First find the last study year (or term) that would be visible solely based on the study right
        const studyYearsBasedOnStudyRight: StudyYear[] = allPossibleStudyYears.filter(
            sy => sy.startYear <= inclusiveTimelineEndYearBasedOnStudyRight,
        );
        const lastStudyYearAndTermBasedOnStudyRight: LastStudyYearAndTerm = findLastStudyYearAndTermBasedOnStudyRight(
            studyRightEndDate,
            studyYearsBasedOnStudyRight,
        );

        // 2. Then find the term including the last planned period
        const lastStudyYearAndTermBasedOnLastPlannedPeriod: LastStudyYearAndTerm = findLastStudyYearAndTermBasedOnLastPlannedPeriod(
            lastPlannedPeriodLocator,
            allPossibleStudyYears,
        );

        // 3. Check which one is later
        const lastStudyYearAndTerm: LastStudyYearAndTerm = getLaterStudyYearAndTerm(
            lastStudyYearAndTermBasedOnStudyRight,
            lastStudyYearAndTermBasedOnLastPlannedPeriod,
        );

        if (!lastStudyYearAndTerm.lastStudyYear) {
            return [];
        }

        const filteredStudyYears: StudyYear[] = takeUntilExactMatch(allPossibleStudyYears, lastStudyYearAndTerm.lastStudyYear);

        // if an explicit last study term is defined, recreate the containing study year
        return lastStudyYearAndTerm.explicitLastStudyTerm
            ? filteredStudyYears.map(studyYear =>
                  studyYear === lastStudyYearAndTerm.lastStudyYear
                      ? {
                            ...studyYear,
                            studyTerms: takeUntilExactMatch(studyYear.studyTerms, lastStudyYearAndTerm.explicitLastStudyTerm),
                        }
                      : studyYear,
              )
            : filteredStudyYears;
    }
}

function getLaterStudyYearAndTerm(a: LastStudyYearAndTerm, b: LastStudyYearAndTerm): LastStudyYearAndTerm {
    if (!a.lastStudyYear) {
        return b;
    }

    if (!b.lastStudyYear) {
        return a;
    }

    if (a.lastStudyYear !== b.lastStudyYear) {
        const aYearStartDate: moment.Moment = dateUtils.createMoment(a.lastStudyYear.valid.startDate);
        const bYearStartDate: moment.Moment = dateUtils.createMoment(b.lastStudyYear.valid.startDate);

        if (aYearStartDate.isAfter(bYearStartDate)) {
            return a;
        }

        if (bYearStartDate.isAfter(aYearStartDate)) {
            return b;
        }
    }

    // "whole" study year is considered later than one with explicit term
    if (!a.explicitLastStudyTerm) {
        return a;
    }

    if (!b.explicitLastStudyTerm) {
        return b;
    }

    if (a.explicitLastStudyTerm !== b.explicitLastStudyTerm) {
        const aTermStartDate: moment.Moment = dateUtils.createMoment(a.explicitLastStudyTerm.valid.startDate);
        const bTermStartDate: moment.Moment = dateUtils.createMoment(b.explicitLastStudyTerm.valid.startDate);

        if (aTermStartDate.isAfter(bTermStartDate)) {
            return a;
        }

        if (bTermStartDate.isAfter(aTermStartDate)) {
            return b;
        }
    }

    // otherwise, it doesn't matter which one we return
    return a;
}

function takeUntilExactMatch<T>(src: readonly T[], lastItem: T): T[] {
    const result: T[] = [];

    for (const srcItem of src) {
        result.push(srcItem);

        if (srcItem === lastItem) {
            break;
        }
    }

    return result;
}

function findLastStudyYearAndTermBasedOnStudyRight(
    studyRightEndDate: moment.Moment | null,
    studyYears: readonly StudyYear[],
): LastStudyYearAndTerm {
    let lastStudyYear: StudyYear | null = null;
    let explicitLastStudyTerm: StudyTerm | null = null; // null stands for the last term of lastStudyYear

    if (!studyYears.length) {
        return {
            lastStudyYear,
            explicitLastStudyTerm,
        };
    }

    if (studyRightEndDate) {
        let termCountLeftToIncludeAfterStudyRight: number = STUDY_TERMS_VISIBLE_AFTER_STUDY_RIGHT;
        for (const studyYear of studyYears) {
            lastStudyYear = studyYear;

            if (dateUtils.createMoment(studyYear.valid.endDate).isAfter(studyRightEndDate)) {
                const termsStartingAfterStudyRight: StudyTerm[] = studyYear.studyTerms.filter(studyTerm =>
                    dateUtils.createMoment(studyTerm.valid.startDate).isSameOrAfter(studyRightEndDate),
                );

                if (termsStartingAfterStudyRight.length) {
                    // only partial year fits
                    if (termsStartingAfterStudyRight.length > termCountLeftToIncludeAfterStudyRight) {
                        explicitLastStudyTerm = termsStartingAfterStudyRight[termCountLeftToIncludeAfterStudyRight - 1];
                        termCountLeftToIncludeAfterStudyRight = 0;
                    }

                    // whole year fits
                    else {
                        termCountLeftToIncludeAfterStudyRight -= termsStartingAfterStudyRight.length;
                    }

                    if (termCountLeftToIncludeAfterStudyRight === 0) {
                        // we're done
                        break;
                    }
                }
            }
        }
    } else {
        lastStudyYear = studyYears[studyYears.length - 1];
    }

    return {
        lastStudyYear,
        explicitLastStudyTerm,
    };
}

function findLastStudyYearAndTermBasedOnLastPlannedPeriod(
    lastPlannedPeriodLocator: StudyPeriodLocator | null,
    studyYears: readonly StudyYear[],
): LastStudyYearAndTerm {
    let lastStudyYear: StudyYear | null = null;
    let explicitLastStudyTerm: StudyTerm | null = null; // null stands for the last term of lastStudyYear

    if (lastPlannedPeriodLocator) {
        for (const studyYear of studyYears) {
            const termIncludingLastPlannedPeriod: StudyTerm | undefined = studyYear.studyTerms.find(
                studyTerm => !!studyTerm.studyPeriods.find(studyPeriod => studyPeriod.locator === lastPlannedPeriodLocator),
            );
            if (termIncludingLastPlannedPeriod) {
                lastStudyYear = studyYear;
                explicitLastStudyTerm = termIncludingLastPlannedPeriod;

                break;
            }
        }
    }

    return {
        lastStudyYear,
        explicitLastStudyTerm,
    };
}

interface LastStudyYearAndTerm {
    readonly lastStudyYear: StudyYear | null;

    /**
     * null stands for the last term of {@link lastStudyYear}.
     */
    readonly explicitLastStudyTerm: StudyTerm | null;
}
